import { useCallback } from 'react';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import { usePeopleManagementApi } from 'infrastructure/api';
import { useCacheService } from 'framework/cache';
import { useShowContext } from 'framework/show';
import { EXPERTISE_CACHE_NAME, ExpertiseErrorMessage, ExpertiseSuccessMessage } from './expertiseService.model';
import { PaginatedResponseDto } from 'framework/api';
import {
  AddExpertiseDto,
  CreateExpertiseDto,
  EmployeeExpertise,
  ExpertiseSearchRequestDto,
  ExpertiseSuggestionsDto,
  UpdateExpertiseDto,
} from './expertiseService.types';
import { useMessageService, ErrorMessage, SuccessMessage } from 'framework/ui/Message';

const useExpertiseService = () => {
  const { sendGetRequest, sendPostRequest, sendPutRequest, sendDeleteRequest } = usePeopleManagementApi();

  const employeeIdFromRouter = useEmployeeId();
  const { data: employeeData } = useShowContext();
  const { addMessage } = useMessageService();

  const currentAlias = employeeIdFromRouter || employeeData?.alias;

  const { addToCache, getValue, cache, invalidateCache } = useCacheService(EXPERTISE_CACHE_NAME);
  const getCacheId = (employeeId: Nullable<string>) => `employee-${employeeId}-expertise`;

  const findAllEmployeeExpertise = useCallback(
    async () => {
      const cacheId = getCacheId(currentAlias);
      const cachedValue: PaginatedResponseDto<EmployeeExpertise> = getValue(cacheId);

      if (cachedValue) {
        return cachedValue;
      } else {
        const url = `/employees/${currentAlias}/expertise`;

        try {
          const response = await sendGetRequest(url);
          const data: PaginatedResponseDto<EmployeeExpertise> = await response.json();
          addToCache(cacheId, data);
        } catch (err) {
          addMessage(new ErrorMessage(ExpertiseErrorMessage.REQUEST));
        }
      }
    },
    // Suppressed warnings because we only need to call useCallback callback if cache is changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cache],
  );

  const addEmployeeExpertise = async (data: AddExpertiseDto) => {
    const url = `/employees/${currentAlias}/expertise/assign`;

    try {
      const response = await sendPostRequest(url, { ...data, employeeAlias: currentAlias });
      const json = await response.json();
      invalidateCache();
      addMessage(new SuccessMessage(ExpertiseSuccessMessage.ADD));
      return json;
    } catch (err) {
      addMessage(new ErrorMessage(ExpertiseErrorMessage.ADD));
    }
  };

  const findExpertiseSuggestions = async (request: ExpertiseSearchRequestDto): Promise<ExpertiseSuggestionsDto> => {
    try {
      const response = await sendPostRequest('/expertise-definitions/search', request);
      const json = await response.json();
      return json;
    } catch {
      addMessage(new ErrorMessage(ExpertiseErrorMessage.SEARCH));
      return { result: [], totalPages: 0 };
    }
  };

  const updateEmployeeExpertise = async (data: UpdateExpertiseDto) => {
    try {
      const url = `/employees/${currentAlias}/expertise/${data.employeeExpertiseId}`;
      const response = await sendPutRequest(url, { ...data, employeeAlias: currentAlias });
      const json = await response.json();

      invalidateCache();
      addMessage(new SuccessMessage(ExpertiseSuccessMessage.UPDATE));
      return json;
    } catch {
      addMessage(new ErrorMessage(ExpertiseErrorMessage.UPDATE));
    }
  };

  const deleteEmployeeExpertise = async (id: string) => {
    try {
      await sendDeleteRequest(`/employees/${currentAlias}/expertise/${id}`);
      invalidateCache();
      addMessage(new SuccessMessage(ExpertiseSuccessMessage.DELETE));
    } catch {
      addMessage(new ErrorMessage(ExpertiseErrorMessage.DELETE));
    }
  };

  const createExpertise = async (data: CreateExpertiseDto) => {
    try {
      const response = await sendPostRequest('/expertise-definitions', data);
      addMessage(new SuccessMessage(ExpertiseSuccessMessage.CREATE));
      return response.json();
    } catch {
      addMessage(new ErrorMessage(ExpertiseErrorMessage.CREATE));
    }
  };

  return {
    findAllEmployeeExpertise,
    addEmployeeExpertise,
    findExpertiseSuggestions,
    updateEmployeeExpertise,
    deleteEmployeeExpertise,
    createExpertise,
  };
};

export default useExpertiseService;
